/**
 * "WHAT TO DO TODAY" - the Overview's list of what is waiting on the author. The prototype
 * draws four rows (`OV_ACTIONS`), and `docs/ui/04-pages.md` recorded the block as PARTIALLY
 * backed: three rows derived, "5 comments are waiting for a reply" did not. Since decision-log row
 * 213 that one is served; one of the other three, looked at under the rule below, is not.
 *
 * ★ EVERY NUMBER IS ONE THE PLATFORM COUNTED. A row is drawn only from a server-given `total`: a
 * list read for ONE row, whose page says how many rows its filter matches across every page. Never
 * the length of a first page - that is a number the platform never said.
 *
 *   ROW (plural form)                     OPERATION, FILTER - and the page's `total`     LINKS TO
 *   "N articles need changes"             listOwnArticles status=changes_requested       /articles
 *   "N comments are waiting for a reply"  listCommentsOnMyArticles unansweredOnly=true   /comments
 *   "N messages are waiting for a reply"  listSupporterMessages unansweredOnly=true      /messages
 *   "N articles are sitting in drafts"    listOwnArticles status=draft                   /articles
 *
 * ★ VIDEOS (v1.0.29) ARE ASKED APART: `listOwnArticles` lists one kind at a time, articles when
 * none is named, so a video sent back for changes was in no count. "N videos need changes" and
 * "N videos are sitting in drafts" ask the same with `kind=video`, and link to the list's Videos
 * tab (`VIDEOS_HREF`) - the plain `/articles` opens on the articles, where a video is not shown.
 *
 * The first three are the sidebar's counts, asked through the very same functions
 * (`features/shell/cabinet-attention.ts`, which also says why each surface sends its own request).
 * Each row links to the screen that resolves it: Articles opens the article's editor; the Inbox's
 * Comments and Messages tabs (B2-INBOX) answer the rest. The link lands on the whole list.
 *
 * ★ LEFT OUT: "Your July statement is ready to download". `listStatements` serves the statements
 * newest first - a period, the settled figure, a payout status (authors-api.yaml, :6395)
 * - and NOTHING that says this author has not opened one yet, and no count. Its newest row would
 * read "ready" every day until the next month's lands: a permanent line, not attention.
 * `listNotifications` keeps a read state, which the cabinet's feed (`/notifications`) shows, but
 * its page carries no `total` by rule - an append-heavy stream (authors-api.yaml) - so
 * an unread statement notice would mean scanning a first page in the browser, which this block
 * does not do. The platform item, if the row is wanted: a served "unopened statements" count, or
 * a `total` on the unread notification filter.
 *
 * EACH ROW HAS ITS OWN READ AND ITS OWN STATE (`useAsync`, as the screen's other two reads):
 *   known, above 0   the row is drawn;
 *   known, 0         no row - there is nothing to do;
 *   not served       no row, and no zero: a page without a `total` is an answer that carried none;
 *   failed           a note says which count did not load (`PartialFailureNote`); the rows that did
 *                    load stay, so one failure never blanks the others;
 *   loading          the screen's loading line, under whatever has already arrived.
 * With every count known and every one 0, one quiet sentence says exactly that. With some counts
 * unserved and the rest 0 the block is not drawn at all: "nothing needs you" would be a claim about
 * a count this app does not have.
 *
 * ★ THE CREATOR TERMS COME FIRST, WHEN THEY WAIT (design 14, F11). The walk of 2026-10-01 found
 * this block listing one draft while the author's current terms were not accepted - the one thing
 * on the page that stopped them sending anything. So when they are owed the block opens with
 *   "Accept the updated agreements - until then you cannot submit an article for review" → /terms
 * and its line says what it does NOT touch: earnings and payouts (D31.3 does not rule withholding
 * money over an unclicked checkbox). It is not a count and has no read of its own: the screen
 * hands in `termsOwed`, decided from the session the app already holds (`termsAcceptanceOwed`,
 * `features/shell/cabinet-attention.ts`, says why that and not a second request). Owed, the block
 * is drawn even when every count is 0 or unserved; the quiet sentence below still speaks only of
 * the four counts, which is all it ever claimed.
 *
 * ★ A NEW AUTHOR IS TOLD WHERE TO START (the owner's live walk of real cabinets, 2026-10-01). An
 * author with no article at all - none in any state - saw "No article needs changes, no comment is
 * waiting for a reply, and you have no drafts" and nothing about what to do first. So when the
 * platform's own count of ALL their articles (`listOwnArticles` with no filter, one row, its
 * `total`) is 0, the block says "Write your first article", linking to My articles, where a draft is
 * started, and the quiet sentence is not drawn under it. It is not a count shown, so a failed or
 * unserved read draws nothing and no note; and a disabled author is never invited (the screen
 * hands in `invitesFirstArticle`).
 *
 * ACCESSIBILITY. A `<section>` named by its `<h2>`; the rows are a list; each row's link IS its
 * sentence, so its accessible name is the count and what it means - "3 comments are waiting for a
 * reply" - and it is a real `<a href>` a plain click follows in place
 * (`features/shell/InAppAnchor.tsx`).
 * "Needs action" is WORDS: the row's dot is decoration (`aria-hidden`), so no colour carries a
 * meaning the words do not.
 *
 * ★ THE PROTOTYPE'S LOOK (design 15 - the owner's live look at real cabinets, 2026-10-01: "adapt
 * UI/styles/layouts to the prototype"). The block is the prototype's `OV_ACTIONS` card: the small
 * capitals "WHAT TO DO TODAY" (`SectionLabel`), then one row per line (`ActionList` /
 * `ActionRow`, ../../features/cabinet/components/kit.tsx) - a dot, the sentence with its status
 * under it, and an outlined pill at the right with the one thing to do: "Accept", "Review",
 * "Reply", "Start", "Continue". The dot is red for something to do (the terms, changes asked for,
 * comments or messages waiting), gold for whenever-you-like (first article, drafts). The pill goes
 * where the sentence goes, so it is for the eye only: `ActionRow` hides it from the accessibility
 * tree and it is taken out of the tab order here (`tabIndex={-1}`) - a keyboard reaches each row
 * once, by its sentence. Nothing about WHAT is drawn changed: the same reads, the same rule for
 * each count, the same quiet sentence, notes and loading line.
 *
 * ★ PAYMENT DETAILS AND "GETTING STARTED" (design 25, section 6; design 24, T22 D1 and D6). Two
 * rows that are not counts, after the agreements and before the first article, each from its own
 * read and drawing nothing when it fails, as the first-article row does:
 *   - "Add your payment details" while the record is not complete (`getPayoutMethod`'s
 *     `complete`, `isComplete` of routes/payments/payment-details.ts); once the payout policy names
 *     the day they are needed by, "Finish your payment details by Jan 1, 2027", and from that day
 *     "payouts wait". The day is compared with the SERVER's today - the payments view's
 *     `computedAt`, handed in as `today` - never a clock. Linking to the Payment details page. Not
 *     for a disabled author, who can read the record and not change it (`asksForPaymentDetails`).
 *   - "Getting started" for an applicant, while the author profile is not sent or Payment details
 *     are not complete, linking to the welcome page's two steps (`/welcome`) - where "Skip for now"
 *     said they would wait. It names what is left, in place of the Payment details row. The profile
 *     is sent once the application is in review or decided - `getApplication`'s own `state`, read
 *     for an applicant only, as `GET /v1/me` sends none; drawn once both its reads have answered.
 */
import { Button, Stack, Text } from '@mantine/core';
import { useCallback, useId } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import type { CabinetApi, OperationResult } from '../../features/cabinet/api';
import type { PaymentDetails } from '../../features/cabinet/api/cabinet-api';
import {
  ActionList,
  ActionRow,
  LoadingState,
  PartialFailureNote,
  SectionLabel,
} from '../../features/cabinet/components';
import { formatDay } from '../../features/cabinet/format';
import { useAsync } from '../../features/cabinet/state';
import type { AsyncResult } from '../../features/cabinet/state';
import {
  ARTICLES_PATH,
  COMMENTS_PATH,
  TERMS_ATTENTION_HREF,
  countArticlesNeedingChanges,
  countCommentsAwaitingReply,
  countMessagesAwaitingReply,
  countVideosNeedingChanges,
} from '../../features/shell/cabinet-attention';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { followsInApp, useNavigateToHref } from '../../features/shell/in-app-navigation';
import { MESSAGES_PATH } from '../messages';
import { VIDEOS_HREF } from '../videos/paths';
import { PAYOUT_METHOD_PATH } from '../payments/PayoutMethodRoute';
import { isComplete } from '../payments/payment-details';
import { WELCOME_PATH } from '../welcome/paths';
import { i18n, useTranslation } from '../../i18n';
import type { TFunction } from 'i18next';
import classes from './overview.module.css';

/** One row of the block: the question it asks the platform, and how its answer is worded. */
export interface AttentionRow {
  readonly key: string;
  /** The platform's own count, or `undefined` when the page carried none. Rejects on failure. */
  readonly read: (api: CabinetApi, signal: AbortSignal) => Promise<number | undefined>;
  /** The row's sentence and link text - the count and what it means. */
  readonly says: (count: number) => string;
  /** The line under it. */
  readonly status: string;
  /** Whether the row's dot is red (something to do) or gold (whenever you like). The words say it. */
  readonly needsAction: boolean;
  /** The screen that resolves it. */
  readonly href: string;
  /** The pill at the right: the one thing to do there - "Review", "Reply", "Continue". */
  readonly act: string;
  /** What a failed read is called: "<what> did not load". */
  readonly what: string;
}

/** The author's drafts: `listOwnArticles` for ONE row of `draft`, and the page's own `total`. */
async function countDrafts(api: CabinetApi, signal: AbortSignal): Promise<number | undefined> {
  return (await api.listOwnArticles({ status: ['draft'], limit: 1 }, signal)).total;
}

/** The author's video drafts, asked the same way of the videos. */
async function countVideoDrafts(api: CabinetApi, signal: AbortSignal): Promise<number | undefined> {
  return (await api.listOwnArticles({ kind: 'video', status: ['draft'], limit: 1 }, signal)).total;
}

/** Where a counted row's words are in the catalogue: `attention.<words>.says|status|act|what`. */
type RowWords = 'changesRequested' | 'unansweredComments' | 'unansweredMessages' | 'drafts';

/**
 * A counted row. Its words are getters, read from the catalogue each time the row is drawn, so the
 * row follows the cabinet's language and nothing is translated when this module loads.
 */
function attentionRow(
  fixed: Pick<AttentionRow, 'key' | 'read' | 'needsAction' | 'href'>,
  words: RowWords,
): AttentionRow {
  return {
    ...fixed,
    says: (count) => i18n.t(`dashboard:attention.${words}.says`, { count }),
    get status() {
      return i18n.t(`dashboard:attention.${words}.status`);
    },
    get act() {
      return i18n.t(`dashboard:attention.${words}.act`);
    },
    get what() {
      return i18n.t(`dashboard:attention.${words}.what`);
    },
  };
}

/** The rows asked of the videos too: their words name the piece, so each has a `_video` variant. */
type VideoRowWords = 'changesRequested' | 'drafts';

/** A videos' row: what names the piece reads its `_video` variant; the status and pill are shared. */
function videoAttentionRow(
  fixed: Pick<AttentionRow, 'key' | 'read' | 'needsAction' | 'href'>,
  words: VideoRowWords,
): AttentionRow {
  return {
    ...fixed,
    says: (count) => i18n.t(`dashboard:attention.${words}.says`, { count, context: 'video' }),
    get status() {
      return i18n.t(`dashboard:attention.${words}.status`);
    },
    get act() {
      return i18n.t(`dashboard:attention.${words}.act`);
    },
    get what() {
      return i18n.t(`dashboard:attention.${words}.what`, { context: 'video' });
    },
  };
}

export const CHANGES_REQUESTED_ROW: AttentionRow = attentionRow(
  {
    key: 'changes-requested',
    read: countArticlesNeedingChanges,
    needsAction: true,
    href: ARTICLES_PATH,
  },
  'changesRequested',
);

export const VIDEO_CHANGES_REQUESTED_ROW: AttentionRow = videoAttentionRow(
  {
    key: 'video-changes-requested',
    read: countVideosNeedingChanges,
    needsAction: true,
    href: VIDEOS_HREF,
  },
  'changesRequested',
);

export const UNANSWERED_COMMENTS_ROW: AttentionRow = attentionRow(
  {
    key: 'unanswered-comments',
    read: countCommentsAwaitingReply,
    needsAction: true,
    href: COMMENTS_PATH,
  },
  'unansweredComments',
);

/**
 * B2-INBOX: the readers' messages waiting for the author's one reply - the same one-row read the
 * Inbox's count makes (`countMessagesAwaitingReply`), worded as the comments' row is, and linking
 * to the Inbox's Messages tab as that row links to its Comments tab.
 */
export const UNANSWERED_MESSAGES_ROW: AttentionRow = attentionRow(
  {
    key: 'unanswered-messages',
    read: countMessagesAwaitingReply,
    needsAction: true,
    href: MESSAGES_PATH,
  },
  'unansweredMessages',
);

export const DRAFTS_ROW: AttentionRow = attentionRow(
  { key: 'drafts', read: countDrafts, needsAction: false, href: ARTICLES_PATH },
  'drafts',
);

export const VIDEO_DRAFTS_ROW: AttentionRow = videoAttentionRow(
  { key: 'video-drafts', read: countVideoDrafts, needsAction: false, href: VIDEOS_HREF },
  'drafts',
);

/** The rows, in the order they are drawn - the prototype's, less the one left out above. */
export const NEEDS_ATTENTION_ROWS: readonly AttentionRow[] = [
  CHANGES_REQUESTED_ROW,
  VIDEO_CHANGES_REQUESTED_ROW,
  UNANSWERED_COMMENTS_ROW,
  UNANSWERED_MESSAGES_ROW,
  DRAFTS_ROW,
  VIDEO_DRAFTS_ROW,
];

/**
 * The creator terms, drawn FIRST and only while they are owed. Not an `AttentionRow`: there is no
 * count and no read - see the header.
 */
export const TERMS_ROW = {
  key: 'creator-terms',
  href: TERMS_ATTENTION_HREF,
  get says(): string {
    return i18n.t('dashboard:attention.terms.says');
  },
  get status(): string {
    return i18n.t('dashboard:attention.terms.status');
  },
  get act(): string {
    return i18n.t('dashboard:attention.terms.act');
  },
};

/**
 * "Write your first article", drawn after the terms and before the counts, only while the author has
 * no article at all - see the header.
 */
export const FIRST_ARTICLE_ROW = {
  key: 'first-article',
  href: ARTICLES_PATH,
  get says(): string {
    return i18n.t('dashboard:attention.firstArticle.says');
  },
  get status(): string {
    return i18n.t('dashboard:attention.firstArticle.status');
  },
  get act(): string {
    return i18n.t('dashboard:attention.firstArticle.act');
  },
};

/** Every article the author has, in any state: ONE row of the unfiltered list, and its `total`. */
async function countAllArticles(api: CabinetApi, signal: AbortSignal): Promise<number | undefined> {
  return (await api.listOwnArticles({ limit: 1 }, signal)).total;
}

export interface NeedsAttentionProps {
  /**
   * Whether the current creator terms wait on this author (`termsAcceptanceOwed`). Optional, and
   * absent is "not owed": a screen that has no session draws the three counts as it always did.
   */
  readonly termsOwed?: boolean;
  /** Whether an author with no article is invited to write one. Absent, they are. */
  readonly invitesFirstArticle?: boolean;
  /** Whether Payment details are asked for while not complete. Absent, they are (the header). */
  readonly asksForPaymentDetails?: boolean;
  /** Whether this is an applicant, who has "Getting started" (see the header). Absent, not. */
  readonly gettingStarted?: boolean;
  /** Today, as the server sent it - the payments view's `computedAt` - for the needed-by day. */
  readonly today?: string | undefined;
}

/** One drawn line of the list: the terms, or a count. */
interface ShownLine {
  readonly key: string;
  readonly says: string;
  readonly status: string;
  readonly needsAction: boolean;
  readonly href: string;
  readonly act: string;
}

/** A served count, or none. `data` of the read is never `undefined` once it has answered. */
type Answer = { readonly served: true; readonly count: number } | { readonly served: false };

/** A count the contract allows: every `total` here is `integer, minimum: 0`. */
function answerOf(total: number | undefined): Answer {
  return total !== undefined && Number.isInteger(total) && total >= 0
    ? { served: true, count: total }
    : { served: false };
}

function useAttentionCount(api: CabinetApi, row: AttentionRow): AsyncResult<Answer> {
  const read = useCallback(
    async (signal: AbortSignal) => answerOf(await row.read(api, signal)),
    [api, row],
  );
  return useAsync(read, `attention:${row.key}`);
}

export function NeedsAttention({
  termsOwed = false,
  invitesFirstArticle = true,
  asksForPaymentDetails = true,
  gettingStarted = false,
  today,
}: NeedsAttentionProps) {
  const { t } = useTranslation('dashboard');
  const api = useCabinetApi();
  const headingId = useId();
  const paymentsAsked = asksForPaymentDetails || gettingStarted;
  const readPayment = useCallback(
    async (signal: AbortSignal): Promise<PaymentDetails | null> =>
      paymentsAsked ? api.getPayoutMethod(signal) : null,
    [api, paymentsAsked],
  );
  const payment = useAsync(readPayment, `attention:payment-details:${String(paymentsAsked)}`);
  // Known and not complete. A failed or unread record draws nothing (see the header).
  const record = payment.data ?? null;
  const paymentDue = record !== null && !isComplete(record);
  const application = useApplicationState(api, gettingStarted);
  const applicationState = application.data ?? null;
  const profileWaits = applicationState !== null && profileWaitsOn(applicationState);
  const reminders: readonly ShownLine[] = gettingStarted
    ? answered(payment) && answered(application)
      ? gettingStartedLines(profileWaits, paymentDue, t)
      : []
    : asksForPaymentDetails && record !== null && paymentDue
      ? [paymentDetailsLine(record, today, t)]
      : [];
  const readAll = useCallback(
    async (signal: AbortSignal) => answerOf(await countAllArticles(api, signal)),
    [api],
  );
  const everything = useAsync(readAll, 'attention:first-article');
  const firstArticle =
    invitesFirstArticle && everything.data?.served === true && everything.data.count === 0;

  const reads: ReadonlyArray<{ readonly row: AttentionRow; readonly result: AsyncResult<Answer> }> =
    [
      { row: CHANGES_REQUESTED_ROW, result: useAttentionCount(api, CHANGES_REQUESTED_ROW) },
      {
        row: VIDEO_CHANGES_REQUESTED_ROW,
        result: useAttentionCount(api, VIDEO_CHANGES_REQUESTED_ROW),
      },
      { row: UNANSWERED_COMMENTS_ROW, result: useAttentionCount(api, UNANSWERED_COMMENTS_ROW) },
      { row: UNANSWERED_MESSAGES_ROW, result: useAttentionCount(api, UNANSWERED_MESSAGES_ROW) },
      { row: DRAFTS_ROW, result: useAttentionCount(api, DRAFTS_ROW) },
      { row: VIDEO_DRAFTS_ROW, result: useAttentionCount(api, VIDEO_DRAFTS_ROW) },
    ];

  const counted = reads.flatMap(({ row, result }): ShownLine[] =>
    result.data?.served === true && result.data.count > 0
      ? [
          {
            key: row.key,
            says: row.says(result.data.count),
            status: row.status,
            needsAction: row.needsAction,
            href: row.href,
            act: row.act,
          },
        ]
      : [],
  );
  const shown: readonly ShownLine[] = [
    ...(termsOwed ? [lineOf(TERMS_ROW, true)] : []),
    ...reminders,
    ...(firstArticle ? [lineOf(FIRST_ARTICLE_ROW, false)] : []),
    ...counted,
  ];
  const failed = reads.filter(({ result }) => result.status === 'error');
  const loading = reads.some(
    ({ result }) => result.status === 'loading' && result.data === undefined,
  );
  const allKnownZero = reads.every(
    ({ result }) =>
      result.status === 'ready' && result.data?.served === true && result.data.count === 0,
  );

  if (shown.length === 0 && failed.length === 0 && !loading && !allKnownZero) {
    // Some counts are not served and the rest are 0: saying "nothing" would claim what nobody said.
    return null;
  }

  return (
    <section aria-labelledby={headingId}>
      <SectionLabel id={headingId}>{t('attention.heading')}</SectionLabel>

      <Stack gap="sm">
        {shown.length === 0 ? null : (
          <ActionList>
            {shown.map((line) => (
              <ActionRow
                key={line.key}
                tone={line.needsAction ? 'action' : 'calm'}
                title={
                  // The sentence IS the link, in the row's own type: the pill beside it is the
                  // visible "go", and a link that is the whole line sits in no run of text.
                  <InAppAnchor href={line.href} inherit c="inherit" underline="hover">
                    {line.says}
                  </InAppAnchor>
                }
                status={line.status}
                action={<RowPill href={line.href}>{line.act}</RowPill>}
              />
            ))}
          </ActionList>
        )}

        {allKnownZero && !firstArticle ? (
          <Text size="sm" c="dimmed">
            {t('attention.nothingWaiting')}
          </Text>
        ) : null}

        {failed.length === reads.length ? (
          <PartialFailureNote what={t('attention.listFailed')} error={failed[0]?.result.error} />
        ) : (
          failed.map(({ row, result }) => (
            <PartialFailureNote key={row.key} what={row.what} error={result.error} />
          ))
        )}

        {loading ? <LoadingState label={t('attention.checking')} /> : null}
      </Stack>
    </section>
  );
}

/**
 * The outlined pill at a row's right (design 15). The same address as the row's sentence and the
 * same in-place follow as `InAppAnchor`, but for the eye only: `ActionRow` hides it from the
 * accessibility tree, so it must not take a keyboard stop either - a focusable element inside
 * `aria-hidden` is a stop with no name (axe `aria-hidden-focus`).
 */
function RowPill({ href, children }: { readonly href: string; readonly children: ReactNode }) {
  const navigate = useNavigateToHref();
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!followsInApp(event)) {
      return;
    }
    event.preventDefault();
    navigate(href);
  };
  return (
    <Button
      component="a"
      href={href}
      onClick={follow}
      tabIndex={-1}
      variant="default"
      radius="xl"
      size="xs"
      className={classes.rowPill}
    >
      {children}
    </Button>
  );
}

/* ============================================ Payment details and "Getting started" (design 25) */

/** Where the application stands - `getApplication`'s `state`, the one thing read off it here. */
type ApplicationState = OperationResult<'getApplication'>['state'];

/**
 * Whether an applicant's author profile still waits on them: not sent yet, or sent back for more.
 * Sent means in review or decided - the application's own `state`. The session cannot say it:
 * `GET /v1/me` sends no `applicationState`.
 */
export function profileWaitsOn(state: ApplicationState): boolean {
  return state !== 'in_review' && state !== 'approved' && state !== 'rejected';
}

/**
 * `GET /v1/application`, for an applicant's "Getting started" and for nobody else (`null`, never
 * asked). A failed read leaves `data` undefined, and the row then names no profile part.
 */
function useApplicationState(
  api: CabinetApi,
  applying: boolean,
): AsyncResult<ApplicationState | null> {
  const read = useCallback(
    async (signal: AbortSignal): Promise<ApplicationState | null> =>
      applying ? (await api.getApplication(signal)).state : null,
    [api, applying],
  );
  return useAsync(read, `attention:application:${String(applying)}`);
}

/** A read that has answered, or failed: one row names both reads, so it waits for both. */
function answered(result: AsyncResult<unknown>): boolean {
  return result.status !== 'loading' || result.data !== undefined;
}

/** The terms or the first-article row as a drawn line, its words read now. */
function lineOf(
  row: {
    readonly key: string;
    readonly href: string;
    readonly says: string;
    readonly status: string;
    readonly act: string;
  },
  needsAction: boolean,
): ShownLine {
  return {
    key: row.key,
    says: row.says,
    status: row.status,
    needsAction,
    href: row.href,
    act: row.act,
  };
}

/** The Payment details row, for a record that is not complete (see the header). */
function paymentDetailsLine(
  method: PaymentDetails,
  today: string | undefined,
  t: TFunction<'dashboard'>,
): ShownLine {
  const neededBy = method.requiredFrom ?? null;
  const base = { key: 'payment-details', needsAction: true, href: PAYOUT_METHOD_PATH } as const;
  if (neededBy === null) {
    return {
      ...base,
      says: t('attention.paymentDetails.add.says'),
      status: t('attention.paymentDetails.add.status'),
      act: t('attention.paymentDetails.add.act'),
    };
  }
  // The day as text, `YYYY-MM-DD`, against the server's own today - the rule of
  // routes/payments/payment-details.ts (`requiredFromLine`).
  if (today !== undefined && today.slice(0, 10) >= neededBy) {
    return {
      ...base,
      says: t('attention.paymentDetails.overdue.says'),
      status: t('attention.paymentDetails.overdue.status'),
      act: t('attention.paymentDetails.overdue.act'),
    };
  }
  return {
    ...base,
    says: t('attention.paymentDetails.neededBy.says', { day: formatDay(neededBy) }),
    status: t('attention.paymentDetails.neededBy.status'),
    act: t('attention.paymentDetails.neededBy.act'),
  };
}

/** "Getting started", naming what is left - or nothing, when nothing is. */
function gettingStartedLines(
  profileWaits: boolean,
  paymentDue: boolean,
  t: TFunction<'dashboard'>,
): readonly ShownLine[] {
  const says =
    profileWaits && paymentDue
      ? t('attention.gettingStarted.both')
      : profileWaits
        ? t('attention.gettingStarted.profile')
        : paymentDue
          ? t('attention.gettingStarted.paymentDetails')
          : null;
  return says === null
    ? []
    : [
        {
          key: 'getting-started',
          says,
          status: t('attention.gettingStarted.status'),
          needsAction: false,
          href: WELCOME_PATH,
          act: t('attention.gettingStarted.act'),
        },
      ];
}
